import { useState, useEffect, useRef, useCallback } from 'react';
import { User, AlarmSet } from '../types/alarm';
import { loadClassAlarmSets } from '../utils/storage';
import { getMinutesBeforeAlarm, playAlarmSound, stopAllAlarmSounds } from '../utils/alarmUtils';
import { userDataService } from '../services/userDataService';

interface UseGlobalAlarmServiceProps {
  user: User | null;
  /** false면 팝업/소리 없이 해당 시각만 소비(이미 사용자가 같은 시각·타입 알람을 끈 경우 등) */
  onAlarmPopupOpen: (alarmInfo?: {
    time: string;
    type: '1M' | '3M' | '5M';
    name?: string;
    /** 이번에 맞은 리마인더 구간(3M: 3|2|1). 모달 타이머 전체 길이에 사용 */
    minutesBefore?: number;
  }) => boolean | void;
}

export const useGlobalAlarmService = ({ user, onAlarmPopupOpen }: UseGlobalAlarmServiceProps) => {
  const [alarmSets, setAlarmSets] = useState<AlarmSet[]>([]);
  const [triggeredAlarms, setTriggeredAlarms] = useState<Set<string>>(new Set());
  const [isAlarmRinging, setIsAlarmRinging] = useState(false);
  const [currentAlarm, setCurrentAlarm] = useState<{ time: string; type: '1M' | '3M' | '5M'; name?: string } | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // 알람 세트 정렬 함수
  const sortAlarmSetsByCreationDate = (alarmSets: AlarmSet[]): AlarmSet[] => {
    return alarmSets.sort((a, b) => {
      // 기본 알람세트는 항상 첫 번째 (인덱스 0)
      if (a.id === 'default-alarm-set') return -1;
      if (b.id === 'default-alarm-set') return 1;
      
      // createdAt이 없는 경우 id에서 timestamp 추출 시도
      const aTimestamp = a.createdAt || parseInt(a.id) || 0;
      const bTimestamp = b.createdAt || parseInt(b.id) || 0;
      
      // 오름차순 정렬 (오래된 것부터, 오른쪽 화살표로 최신 접근)
      return aTimestamp - bTimestamp;
    }).map(set => ({
      ...set,
      createdAt: set.createdAt || parseInt(set.id) || 0 // createdAt이 없으면 id에서 추출
    }));
  };

  // 사용자 알람 데이터 로드
  const loadUserAlarmData = useCallback(async () => {
    if (!user?.id) return;

    console.log('🔍 loadUserAlarmData 시작:', { userId: user.id, currentClassId: user.currentClassId });

    try {
      const currentClassId = user.currentClassId;
      if (currentClassId) {
        console.log('📂 Firestore에서 알람세트 로드 시도:', { userId: user.id, classId: currentClassId });
        // Firestore에서 먼저 시도
        try {
          const savedAlarmSets = await userDataService.loadClassAlarmSets(user.id, currentClassId);
          console.log('✅ Firestore에서 로드된 알람세트:', savedAlarmSets);
          
          if (savedAlarmSets.length === 0) {
            const defaultSet: AlarmSet = {
              id: 'default-alarm-set',
              name: '기본 알람',
              alarms: [],
              isActive: true,
              createdAt: Date.now()
            };
            console.log('📝 기본 알람 세트 생성:', defaultSet);
            setAlarmSets([defaultSet]);
            
            // Firestore에 기본 알람 세트 저장
            try {
              await userDataService.saveClassAlarmSets(user.id, currentClassId, [defaultSet]);
              console.log('✅ 기본 알람 세트 Firestore 저장 완료');
            } catch (error) {
              console.error('❌ 기본 알람 세트 Firestore 저장 실패:', error);
            }
          } else {
            const alarmSetsWithClosedExpansion = savedAlarmSets.map(set => ({
              ...set,
              createdAt: (set as any).createdAt || parseInt(set.id) || 0,
              alarms: set.alarms.map(alarm => ({
                ...alarm,
                isExpanded: false
              }))
            }));
            
            // 알람 세트 정렬 적용
            const sortedAlarmSets = sortAlarmSetsByCreationDate(alarmSetsWithClosedExpansion);
            console.log('🎯 정렬된 알람세트:', sortedAlarmSets);
            setAlarmSets(sortedAlarmSets);
          }
        } catch (firebaseError) {
          console.error('❌ Firestore에서 알람 데이터 로드 실패:', firebaseError);
          
          // Firestore 실패 시 로컬 스토리지에서 로드
          const userAlarmSets = loadClassAlarmSets(user.id, currentClassId);
          
          if (userAlarmSets.length === 0) {
            const defaultSet: AlarmSet = {
              id: 'default-alarm-set',
              name: '기본 알람',
              alarms: [],
              isActive: true,
              createdAt: Date.now()
            };
            console.log('📝 기본 알람 세트 생성 (로컬):', defaultSet);
            setAlarmSets([defaultSet]);
            
            // 로컬 스토리지에 기본 알람 세트 저장
            try {
              const { saveClassAlarmSets } = await import('../utils/storage');
              saveClassAlarmSets([defaultSet], user.id, currentClassId);
              console.log('✅ 기본 알람 세트 로컬 저장 완료');
            } catch (error) {
              console.error('❌ 기본 알람 세트 로컬 저장 실패:', error);
            }
          } else {
            const alarmSetsWithClosedExpansion = userAlarmSets.map(set => ({
              ...set,
              alarms: set.alarms.map(alarm => ({
                ...alarm,
                isExpanded: false
              }))
            }));
            
            // 알람 세트 정렬 적용
            const sortedAlarmSets = sortAlarmSetsByCreationDate(alarmSetsWithClosedExpansion);
            console.log('🎯 정렬된 알람세트 (로컬):', sortedAlarmSets);
            setAlarmSets(sortedAlarmSets);
          }
        }
      }
    } catch (error) {
      console.error('알람 데이터 로드 중 오류:', error);
    }
  }, [user]);

  // 활성화된 알람 세트 가져오기
  const getActiveAlarmSet = useCallback(() => {
    return alarmSets.find(set => set.isActive);
  }, [alarmSets]);

  // 알람 소리 재생
  const handleAlarmSound = useCallback((
    type: '1M' | '3M' | '5M',
    category: 'basic' | 'class' = 'basic',
    minutesBefore: number
  ) => {
    if (audioRef.current) {
      audioRef.current.pause();
    }

    const audio = playAlarmSound(type, category, minutesBefore);
    audioRef.current = audio;
    audio.loop = false;
    audio.play().catch(console.error);
    setIsAlarmRinging(true);
  }, []);

  // 알람 체크 함수
  const checkAlarms = useCallback(() => {
    if (!user?.id) return;

    const activeSet = getActiveAlarmSet();
    if (!activeSet) {
      console.log('🔍 알람 체크: 활성 알람 세트가 없음');
      return;
    }

    const now = new Date();
    const currentDay = now.getDay();
    const currentTime = now.toTimeString().slice(0, 5);

    // 날짜가 바뀌면 울린 알람 목록 초기화
    if (triggeredAlarms.size > 0) {
      const lastTriggeredDay = Array.from(triggeredAlarms)[0]?.split('-')[2];
      if (lastTriggeredDay && parseInt(lastTriggeredDay) !== currentDay) {
        setTriggeredAlarms(new Set());
      }
    }

    // 시간이 지난 알람들을 triggeredAlarms에서 제거
    const updatedTriggeredAlarms = new Set(triggeredAlarms);
    let hasRemovedAlarm = false;

    Array.from(triggeredAlarms).forEach(alarmKey => {
      const [time] = alarmKey.split('-');
      const alarmTime = new Date();
      const [hours, minutes] = time.split(':').map(Number);
      alarmTime.setHours(hours, minutes, 0, 0);

      if (now.getTime() >= alarmTime.getTime()) {
        updatedTriggeredAlarms.delete(alarmKey);
        hasRemovedAlarm = true;
      }
    });

    if (hasRemovedAlarm) {
      setTriggeredAlarms(updatedTriggeredAlarms);
      if (updatedTriggeredAlarms.size === 0) {
        setCurrentAlarm(null);
      }
    }

    // 알람 체크
    activeSet.alarms.forEach(alarm => {
      if (!alarm.isActive) return;
      if (!alarm.days.includes(currentDay)) return;

      const minutesBefore = getMinutesBeforeAlarm(alarm);
      if (minutesBefore !== null) {
        // 알람 타입과 남은 시간을 포함한 고유 키 생성
        const alarmKey = `${alarm.time}-${alarm.type}-${currentDay}-${minutesBefore}`;
        if (triggeredAlarms.has(alarmKey)) {
          console.log('🔍 알람 체크: 이미 울린 알람', alarmKey);
          return;
        }

        // 알람 카테고리 결정 (알람의 category 또는 알람세트의 category, 기본값: 'basic')
        const category = alarm.category || activeSet.category || 'basic';
        
        console.log(`🔔 [${alarm.type}] 알람 실행:`, { 
          time: alarm.time, 
          type: alarm.type, 
          name: alarm.name, 
          category, 
          minutesBefore, 
          currentTime, 
          currentDay 
        });

        const openResult = onAlarmPopupOpen({
          time: alarm.time,
          type: alarm.type,
          name: alarm.name,
          minutesBefore
        });
        if (openResult === false) {
          // 팝업·소리 생략 (예: 사용자가 3분전에 알람끄기 → 2분전·1분전 동일 시각·타입 취소)
          setTriggeredAlarms(prev => new Set(Array.from(prev).concat(alarmKey)));
          return;
        }

        setCurrentAlarm({ time: alarm.time, type: alarm.type, name: alarm.name });
        handleAlarmSound(alarm.type, category, minutesBefore);

        // 울린 알람으로 표시
        setTriggeredAlarms(prev => new Set(Array.from(prev).concat(alarmKey)));
      }
    });
  }, [user, triggeredAlarms, getActiveAlarmSet, onAlarmPopupOpen, handleAlarmSound]);

  // 알람 중지
  const stopAlarm = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    setIsAlarmRinging(false);
    setCurrentAlarm(null);
  }, []);

  // 알람 데이터 로드
  useEffect(() => {
    if (!user?.id) return;
    loadUserAlarmData();
  }, [loadUserAlarmData, user?.id]);

  // 알람 체크 인터벌
  useEffect(() => {
    if (!user?.id) return undefined;
    const interval = setInterval(checkAlarms, 1000);
    return () => clearInterval(interval);
  }, [checkAlarms, user?.id]);

  // 컴포넌트 언마운트 시 알람 중지
  useEffect(() => {
    return () => {
      stopAllAlarmSounds();
    };
  }, []);

  return {
    alarmSets,
    setAlarmSets,
    triggeredAlarms,
    isAlarmRinging,
    currentAlarm,
    stopAlarm,
    loadUserAlarmData
  };
}; 